import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { SafeAreaFrameContext, SafeAreaInsetsContext } from 'react-native-safe-area-context'
import { ChatContainer } from './components/chat/ChatContainer'
import { initChatApi, chatApi } from './api/client'
import {
  getApiBaseUrl,
  getApiKey,
  getApiProfile,
  getBrandingRuntimeConfig,
  getEmbedInitFromRuntime,
  getRequireHostInitOverride,
  getSector,
  getWsBaseUrl,
} from './lib/runtimeConfig'
import './index.css'

import { MD3DarkTheme, MD3LightTheme, PaperProvider } from 'react-native-paper'
import {
  applyTenantBrandingToDocument,
  createPaperThemeFromTenantBranding,
  getTenantIdFromRuntime,
  loadTenantConfig,
  tenantConfigToChatConfig,
} from './config/tenantConfig'

import { createFrontendLogger } from './logging/frontendLogger'
import { createHostBridge } from './integration/hostBridge'
import { useChatStore } from './hooks/useChatStore'

import {
  applyHostTenantBrandingToDocument,
  applyHostThemeToDocument,
  extractHostTenantBrandingFromInitPayload,
  extractHostThemeFromInitPayload,
  type HostTenantBranding,
  type HostTheme,
} from './lib/embedTheme'

const frontendLogger = createFrontendLogger({
  endpointUrl: `${getApiBaseUrl()}/api/logs/events`,
  getDefaultContext: () => ({
    service: 'xedu_frontend',
    logger: 'global',
  }),
});

let hostBrandingOverride: HostTenantBranding | undefined;
let hostThemeOverride: HostTheme | undefined;
const runtimeBranding = getBrandingRuntimeConfig();
const runtimeApiProfile = getApiProfile();
const runtimeApiKey = getApiKey();
const runtimeSector = getSector();
const runtimeRequireHostInit = getRequireHostInitOverride();
const runtimeEmbedInit = getEmbedInitFromRuntime();

type ChatRouteMode = 'cs' | 'cs_intake';

function renderPaperIcon(props: any) {
  const { name, color, size, testID } = props as any;

  const stroke = color ?? 'currentColor';
  const commonSvgProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke,
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    role: 'img',
    'aria-label': name,
    ...(testID ? { 'data-testid': testID } : {}),
  };

  switch (name) {
    case 'alert-circle':
      return (
        <svg {...commonSvgProps}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="7" x2="12" y2="13" />
          <circle cx="12" cy="17" r="0.75" fill={stroke} stroke="none" />
        </svg>
      );

    case 'account-plus':
      return (
        <svg {...commonSvgProps}>
          <circle cx="10" cy="8" r="3" />
          <path d="M4 20c0-3 2.7-5.5 6-5.5s6 2.5 6 5.5" />
          <line x1="18" y1="8" x2="18" y2="12" />
          <line x1="16" y1="10" x2="20" y2="10" />
        </svg>
      );

    case 'microphone':
      return (
        <svg {...commonSvgProps}>
          <rect x="9" y="3" width="6" height="10" rx="3" />
          <path d="M8 11a4 4 0 0 0 8 0" />
          <line x1="12" y1="15" x2="12" y2="19" />
          <line x1="9" y1="19" x2="15" y2="19" />
        </svg>
      );

    case 'paperclip':
      return (
        <svg {...commonSvgProps}>
          <path d="M21 8.5l-8.5 8.5a4 4 0 1 1-5.7-5.7L15 3.1a2.5 2.5 0 1 1 3.5 3.5L9.8 15.3a1 1 0 1 1-1.4-1.4l7-7" />
        </svg>
      );

    case 'send':
      return (
        <svg {...commonSvgProps}>
          <path d="M22 2L11 13" />
          <path d="M22 2l-7 20-4-9-9-4 20-7z" />
        </svg>
      );

    case 'school-outline':
      return (
        <svg {...commonSvgProps}>
          <path d="M3 9l9-5 9 5-9 5-9-5z" />
          <path d="M7 11.2v4.1c0 1.7 2.2 3.1 5 3.1s5-1.4 5-3.1v-4.1" />
          <path d="M21 9v5" />
          <circle cx="21" cy="16" r="1" fill={stroke} stroke="none" />
        </svg>
      );

    default:
      // Unknown icon name: render nothing rather than crashing.
      return null;
  }
}

function normalizeChatRoute(): ChatRouteMode {
  // Minimal routing without react-router.
  // Requirements:
  // - Default URL: /
  // - Any URL redirects to /
  // - /chat/staff added later
  // - /intake loads the embedded intake form for testing
  try {
    if (typeof window === 'undefined') return 'cs';

    const path = window.location.pathname || '/';
    const isIntake = path === '/intake' || path.startsWith('/intake/');
    if (isIntake) return 'cs_intake';

    // Backwards-compatible routes.
    if (path === '/chat/cs/intake' || path.startsWith('/chat/cs/intake/')) {
      window.location.replace('/intake');
      return 'cs_intake';
    }
    if (path === '/chat/cs' || path.startsWith('/chat/cs/')) {
      window.location.replace('/');
      return 'cs';
    }

    if (path === '/' || path.startsWith('/?')) return 'cs';

    // For now, any other path is redirected to /. This matches the requested behavior for public users.
    window.location.replace('/');
    return 'cs';
  } catch {
    return 'cs';
  }
}

// Run immediately so refresh/deep-links normalize before the UI mounts.
const __CHAT_ROUTE_MODE__: ChatRouteMode = normalizeChatRoute();

class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error?: unknown }
> {
  state: { error?: unknown } = { error: undefined };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown) {
    console.error('[XEdu UI] Render error:', error);
    try {
      frontendLogger.captureError(error, { source: 'react.error_boundary' })
    } catch {
      // ignore
    }
  }

  render() {
    if (this.state.error) {
      const details =
        this.state.error instanceof Error
          ? `${this.state.error.name}: ${this.state.error.message}\n${this.state.error.stack || ''}`
          : typeof this.state.error === 'string'
            ? this.state.error
            : JSON.stringify(this.state.error);

      return (
        <div style={{ fontFamily: 'ui-sans-serif, system-ui', padding: 16 }}>
          <h1 style={{ fontSize: 18, fontWeight: 600 }}>XEdu UI failed to render</h1>
          <p style={{ marginTop: 8, color: '#444' }}>
            The UI crashed while loading. Check the details below.
          </p>
          <pre
            style={{
              marginTop: 12,
              whiteSpace: 'pre-wrap',
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 8,
              overflow: 'auto',
            }}
          >
            {(details || '').replace(/</g, '&lt;')}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

function showFatalError(title: string, error?: unknown) {
  try {
    const root = document.getElementById('root')
    if (!root) return
    const details =
      error instanceof Error
        ? `${error.name}: ${error.message}\n${error.stack || ''}`
        : typeof error === 'string'
          ? error
          : JSON.stringify(error)

    root.innerHTML = `
      <div style="font-family: ui-sans-serif, system-ui; padding: 16px;">
        <h1 style="font-size: 18px; font-weight: 600;">${title}</h1>
        <p style="margin-top: 8px; color: #444;">The UI crashed while loading. Check the details below.</p>
        <pre style="margin-top: 12px; white-space: pre-wrap; background: #f5f5f5; padding: 12px; border-radius: 8px; overflow: auto;">${(details || '').replace(/</g, '&lt;')}</pre>
      </div>
    `
  } catch {
    // ignore
  }
}

window.addEventListener('error', (event) => {
  showFatalError('XEdu UI runtime error', (event as any).error || (event as any).message)
  try {
    frontendLogger.captureError((event as any).error || (event as any).message, {
      source: 'window.error',
      filename: (event as any).filename,
      lineno: (event as any).lineno,
      colno: (event as any).colno,
    })
  } catch {
    // ignore
  }
})

window.addEventListener('unhandledrejection', (event) => {
  showFatalError('XEdu UI unhandled promise rejection', (event as any).reason)
  try {
    frontendLogger.captureError((event as any).reason, { source: 'window.unhandledrejection' })
  } catch {
    // ignore
  }
})

// Register service worker for PWA.
// NOTE: On localhost, a SW can easily cause confusing “stale bundle” bugs while iterating.
// For local dev we proactively unregister SW + clear caches and skip registration.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    ;(async () => {
      const host = typeof window !== 'undefined' ? window.location.hostname : ''
      const isLocalhost = host === 'localhost'

      if (isLocalhost) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations()
          await Promise.all(regs.map((r) => r.unregister()))
        } catch {
          // ignore
        }

        try {
          if ('caches' in window) {
            const keys = await caches.keys()
            await Promise.all(keys.map((k) => caches.delete(k)))
          }
        } catch {
          // ignore
        }

        return
      }

      try {
        const resp = await fetch('/sw.js', { cache: 'no-store' })
        const contentType = resp.headers.get('content-type') || ''
        const isJavaScript = contentType.includes('javascript')
        if (!resp.ok || !isJavaScript) {
          return
        }

        const registration = await navigator.serviceWorker.register('/sw.js', {
          // Ensure the browser doesn't reuse a cached SW script.
          updateViaCache: 'none',
        })
        await registration.update()
        console.log('Service Worker registered:', registration)
      } catch (error) {
        console.log('Service Worker registration failed:', error)
      }
    })()
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 2, staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  },
});

const apiUrl = getApiBaseUrl();
const wsUrl = getWsBaseUrl();
const safeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };
const safeAreaFrame = {
  x: 0,
  y: 0,
  width: typeof window !== 'undefined' ? window.innerWidth : 0,
  height: typeof window !== 'undefined' ? window.innerHeight : 0,
};

/**
 * Wait up to `timeoutMs` for the Zustand chat store to have a non-null config.
 * The config is set by ChatContainer.useEffect → initialize(config), which fires
 * after React paints. If the host sends INIT before that paint (race), this wait
 * ensures applyHostInit() never sees config=null and never throws "Chat not initialized".
 */
function waitForChatConfig(timeoutMs = 3000): Promise<void> {
  if (useChatStore.getState().config) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let done = false;
    const to = setTimeout(() => {
      if (!done) { done = true; unsub(); resolve(); }
    }, timeoutMs);
    const unsub = useChatStore.subscribe((s) => {
      if (s.config && !done) { done = true; clearTimeout(to); unsub(); resolve(); }
    });
  });
}

function buildAllowedHostOriginRegex(): RegExp {
  try {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    if (host === 'localhost') {
      return /^https?:\/\/localhost(:\d+)?$/i;
    }
  } catch {
    // ignore
  }

  return /^https:\/\/([a-z0-9-]+\.)*xedu\.com\.au$/i;
}

const hostBridge = createHostBridge({
  allowedHostOriginRegex: buildAllowedHostOriginRegex(),
  onInit: async (payload) => {
    // Theme precedence:
    // 1) tenantConfig.branding (primary)
    // 2) theme (secondary/optional)
    // Note: apply theme first, then branding so branding wins for CSS vars.
    const extractedTheme = extractHostThemeFromInitPayload(payload);
    const extractedBranding = extractHostTenantBrandingFromInitPayload(payload);

    if (extractedTheme) {
      hostThemeOverride = extractedTheme;
      applyHostThemeToDocument(extractedTheme);
    }

    if (extractedBranding) {
      hostBrandingOverride = extractedBranding;
      applyHostTenantBrandingToDocument(extractedBranding);
    }

    const anyPayload = payload as any;

    const _isUuidLike = (v: unknown): boolean => {
      const s = (typeof v === 'string' ? v : '').trim();
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    };

    const rawTenantId =
      typeof anyPayload?.tenantConfig?.id === 'string'
        ? anyPayload.tenantConfig.id
        : typeof anyPayload?.tenant?.id === 'string'
          ? anyPayload.tenant.id
        : typeof anyPayload?.tenantId === 'string'
          ? anyPayload.tenantId
          : typeof anyPayload?.tenant_id === 'string'
            ? anyPayload.tenant_id
          : undefined;
    // Only use UUID-like values.
    const extractedTenantId = rawTenantId && _isUuidLike(rawTenantId) ? rawTenantId : undefined;

    // Token shape varies across host apps. Support:
    // - { accessToken }
    // - { access_token }
    // - { token }
    // - { auth: { accessToken | access_token | token } }
    const extractedAccessToken =
      typeof anyPayload?.accessToken === 'string'
        ? anyPayload.accessToken
        : typeof anyPayload?.access_token === 'string'
          ? anyPayload.access_token
          : typeof anyPayload?.token === 'string'
            ? anyPayload.token
            : typeof anyPayload?.auth?.accessToken === 'string'
              ? anyPayload.auth.accessToken
              : typeof anyPayload?.auth?.access_token === 'string'
                ? anyPayload.auth.access_token
                : typeof anyPayload?.auth?.token === 'string'
                  ? anyPayload.auth.token
                  : undefined;

    // Extract user object (includes user_type, id, email, etc.)
    const extractedUser =
      typeof anyPayload?.user === 'object' && anyPayload.user !== null
        ? anyPayload.user
        : undefined;

    // If multiple iframes mount simultaneously, the host may mark secondary iframes with sessionExists=true.
    // Missing/undefined is treated as false (normal welcome flow).
    const extractedSessionExists = anyPayload?.sessionExists === true;
    const extractedConversationId =
      typeof anyPayload?.conversationId === 'string' && _isUuidLike(anyPayload.conversationId)
        ? anyPayload.conversationId
        : typeof anyPayload?.conversation_id === 'string' && _isUuidLike(anyPayload.conversation_id)
          ? anyPayload.conversation_id
          : undefined;

    // Debug logging to see what user context is received from host
    if (typeof console !== 'undefined' && console.log) {
      console.log('[ChatUI INIT] User object from host:', extractedUser);
    }

    // Guard: ChatContainer.useEffect sets config after React paints. If the host's
    // init-fallback timer fires before that paint, config is null and applyHostInit
    // would throw "Chat not initialized". Wait up to 3s for config to be ready.
    await waitForChatConfig(3000);

    await useChatStore.getState().applyHostInit({
      tenantId: extractedTenantId,
      accessToken: extractedAccessToken,
      user: extractedUser,
      sessionExists: extractedSessionExists,
      conversationId: extractedConversationId,
    });
  },
  onInitiate: async (payload) => {
    try {
      const res = await useChatStore.getState().initiateFromHost(payload);

      if (hostBridge.isEmbedded && res && Array.isArray((res as any).helpHints) && (res as any).helpHints.length > 0) {
        try {
          hostBridge.postHelpHints({
            conversationId: (res as any).conversationId,
            topic: (res as any).topic,
            helpHints: (res as any).helpHints,
            ts: Date.now(),
          });
        } catch {
          // ignore
        }
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error('INITIATE failed');
      try {
        hostBridge.postError('INITIATE failed', { message: err.message });
      } catch {
        // ignore
      }
    }
  },
  onError: (err) => {
    try {
      hostBridge.postError('INIT failed', { message: err.message });
    } catch {
      // ignore
    }
  },
});

// Allow CSS to switch backgrounds when embedded.
try {
  if (typeof document !== 'undefined') {
    if (hostBridge.isEmbedded) {
      document.documentElement.dataset.embedded = 'true';
    }
  }
} catch {
  // ignore
}

async function buildChatConfig() {
  const tenantId = getTenantIdFromRuntime();
  const tenantConfig = await loadTenantConfig(tenantId);
  applyTenantBrandingToDocument(tenantConfig);

  const mapped = tenantConfigToChatConfig(tenantConfig, tenantId);
  const resolvedTenantId = mapped.tenantId || tenantId;

  initChatApi({
    baseURL: apiUrl,
    profile: runtimeApiProfile,
    sector: runtimeSector,
    headers: {
      'X-Tenant-ID': resolvedTenantId,
      ...(runtimeApiKey ? { 'X-API-Key': runtimeApiKey } : {}),
    },
  });

  if (runtimeEmbedInit?.tenantId) {
    chatApi.setTenantId(runtimeEmbedInit.tenantId);
  }
  if (runtimeEmbedInit?.accessToken && runtimeApiProfile !== 'adhd_external') {
    let token = runtimeEmbedInit.accessToken.trim();
    if (token.toLowerCase().startsWith('bearer ')) {
      token = token.slice(7).trim();
    }
    if (
      (token.startsWith('"') && token.endsWith('"')) ||
      (token.startsWith("'") && token.endsWith("'"))
    ) {
      token = token.slice(1, -1).trim();
    }
    if (token.length > 8) {
      chatApi.setAuthToken(token);
      try {
        window.localStorage.setItem('xedu_auth_token_v1', token);
      } catch {
        // ignore storage failures
      }
    }
  }

  // Restore persisted auth token immediately after API client is created so
  // that any requests made before HOST_INIT arrives already have auth headers.
  // This prevents the staff-role flip when the chat is reopened after minimize.
  try {
    if (runtimeApiProfile === 'adhd_external') {
      chatApi.clearAuthToken();
    } else {
      const storedToken = window.localStorage.getItem('xedu_auth_token_v1');
      if (!runtimeEmbedInit?.accessToken && storedToken && storedToken.length > 8) {
        chatApi.setAuthToken(storedToken.trim());
      }
    }
  } catch {
    // ignore storage failures
  }

  const mergedBranding = {
    ...(tenantConfig?.branding || {}),
    ...(hostBrandingOverride || {}),
  };

  const hostColors = hostThemeOverride?.colors;

  const chatConfig = {
    apiUrl,
    wsUrl,
    tenantId: resolvedTenantId,
    sector: runtimeSector,
    apiProfile: runtimeApiProfile,
    apiKey: runtimeApiKey || undefined,
    theme: {
      primaryColor: runtimeBranding.primaryColor || mergedBranding.primaryColor || hostColors?.primary || mapped.theme.primaryColor || '#146B55',
      secondaryColor: runtimeBranding.secondaryColor || mergedBranding.secondaryColor || hostColors?.secondary || mapped.theme.secondaryColor || '#1F6F8B',
      accentColor: runtimeBranding.accentColor || mergedBranding.accentColor || hostColors?.accent || mapped.theme.accentColor || '#D99A16',
      fontFamily: mergedBranding.fontFamily || mapped.theme.fontFamily,
    },
    branding: {
      ...(mapped.branding || {}),
      ...(mergedBranding || {}),
      ...(runtimeBranding.title ? { title: runtimeBranding.title } : {}),
      ...(runtimeBranding.subtitle ? { subtitle: runtimeBranding.subtitle } : {}),
    },
    features: {
      voiceInput: false,
      fileUpload: true,
      embeddedForms: true,
      embeddedTables: true,
      embeddedCharts: true,
      embeddedCalendar: true,
    },
    behavior: {
      autoFocus: true,
      persistHistory: true,
      typingIndicator: true,
      readReceipts: false,
      soundNotifications: false,
      maxMessages: 100,
      requireHostInit: runtimeRequireHostInit ?? hostBridge.isEmbedded,
    },
    callbacks: {
      onMessage: (m: any) => console.log('[Chat] Message:', m),
      onUiAction: (action: any) => {
        if (!hostBridge.isEmbedded) return;
        hostBridge.postAction(action);
      },
      onError: (e: any) => {
        console.error('[Chat] Error:', e);
        if (hostBridge.isEmbedded) {
          hostBridge.postError('Chat error', { message: e?.message || String(e) });
        }
      },
      onReady: () => {
        console.log('[Chat] Ready');
        if (!hostBridge.isEmbedded) return;
        hostBridge.postReady();

        try {
          const rootEl = document.getElementById('root');
          if (rootEl) {
            hostBridge.startResizeObserver(rootEl);
          }
        } catch {
          // ignore
        }
      },
    },
  };

  const baseMdTheme = hostThemeOverride?.mode === 'dark' ? MD3DarkTheme : MD3LightTheme;

  // Base: merged branding (host branding takes precedence over fetched tenant branding).
  const paperBase = createPaperThemeFromTenantBranding(baseMdTheme, mergedBranding);

  // Secondary: optional host theme can fill gaps, but should not override branding palette.
  const paperTheme = hostThemeOverride
    ? {
        ...paperBase,
        roundness:
          typeof mergedBranding?.borderRadius === 'number'
            ? paperBase.roundness
            : typeof hostThemeOverride?.borderRadius?.md === 'number'
              ? hostThemeOverride.borderRadius.md
              : paperBase.roundness,
        colors: {
          ...paperBase.colors,
          primary:
            !mergedBranding?.primaryColor && hostColors?.primary
              ? hostColors.primary
              : paperBase.colors.primary,
          secondary:
            !mergedBranding?.secondaryColor && hostColors?.secondary
              ? hostColors.secondary
              : paperBase.colors.secondary,
          tertiary:
            !mergedBranding?.accentColor && hostColors?.accent
              ? hostColors.accent
              : paperBase.colors.tertiary,
          background:
            !mergedBranding?.backgroundColor && hostColors?.background
              ? hostColors.background
              : paperBase.colors.background,
          surface: hostColors?.surface || paperBase.colors.surface,
          onSurface: hostColors?.text || paperBase.colors.onSurface,
          outline: hostColors?.border || paperBase.colors.outline,
        },
      }
    : paperBase;

  return { chatConfig, paperTheme };
}

try {
  console.log('[BOOT] main.tsx executing');
  console.log('[BOOT] route mode:', __CHAT_ROUTE_MODE__);
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Missing #root element');
  }
  rootEl.textContent = 'Booting UI…';
  console.log('[BOOT] #root found, creating React root');

  const root = ReactDOM.createRoot(rootEl);

  // Render a minimal placeholder immediately.
  root.render(
    <div
      style={{
        fontFamily: 'ui-sans-serif, system-ui',
        height: typeof window !== 'undefined' ? `${window.innerHeight}px` : '100%',
        width: '100%',
        margin: 0,
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      Loading…
    </div>
  );

  ;(async () => {
    const { chatConfig, paperTheme } = await buildChatConfig();

    // Initialize the Zustand store before React renders so host INIT can never
    // observe config=null while waiting for ChatContainer to mount/effect.
    useChatStore.getState().initialize(chatConfig);

    root.render(
      <React.StrictMode>
        <RootErrorBoundary>
          <SafeAreaFrameContext.Provider value={safeAreaFrame}>
            <SafeAreaInsetsContext.Provider value={safeAreaInsets}>
              <QueryClientProvider client={queryClient}>
                <PaperProvider theme={paperTheme} settings={{ icon: renderPaperIcon }}>
                  <div
                    style={{
                      height: typeof window !== 'undefined' ? `${window.innerHeight}px` : '100%',
                      width: '100%',
                      margin: 0,
                      padding: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ width: '100%', height: '100%', flex: 1, minHeight: 0 }}>
                      <React.Suspense fallback={<div style={{ padding: 12 }}>Loading chat…</div>}>
                        <ChatContainer config={chatConfig} />
                      </React.Suspense>
                    </div>
                  </div>
                </PaperProvider>
              </QueryClientProvider>
            </SafeAreaInsetsContext.Provider>
          </SafeAreaFrameContext.Provider>
        </RootErrorBoundary>
      </React.StrictMode>
    );
  })().catch((error) => {
    showFatalError('XEdu UI boot error', error)
    try {
      frontendLogger.captureError(error, { source: 'boot' })
    } catch {
      // ignore
    }
  })
} catch (error) {
  showFatalError('XEdu UI boot error', error)
  try {
    frontendLogger.captureError(error, { source: 'boot.sync' })
  } catch {
    // ignore
  }
}
